import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import { handleApiCall } from "../../apis";
import { endpoint } from "../../utils/endpoints";
import Toastify from "../../utils/tostfy";
import {
  limitTextInput,
  validateWordCount,
  WORD_COUNT_HINT,
} from "../../utils/textWordValidation";

import { formatDateTime } from "../../utils/formatDateTime";
import { formatInr, truncateAmountParts } from "../../utils/formatAmount";

const formatLitersTruncated = (value) => {
  const parts = truncateAmountParts(value);
  if (!parts) return "0.00";
  return `${parts.sign}${parts.intPart}.${parts.fraction}`;
};

const resolveTripAwardRate = (data) => {
  const n = Number(data?.trip_awards?.amount);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const resolveDieselAwardLiters = (details) => {
  const list = Array.isArray(details) ? details : [];
  for (const row of list) {
    const diesel = Number(row?.diesel_award);
    if (Number.isFinite(diesel)) return diesel;
  }
  return null;
};

const computeMaxWaiveLiters = (dieselAward, drivers, rate) => {
  if (!Number.isFinite(dieselAward) || dieselAward >= 0) return 0;
  const driverList = (Array.isArray(drivers) ? drivers : [drivers]).filter(Boolean);
  const maxFromDiesel = Math.abs(dieselAward);
  if (!rate) return maxFromDiesel;
  const absBal = driverList.reduce(
    (sum, driver) => sum + Math.abs(Number(driver?.awardAmount) || 0),
    0
  );
  if (absBal <= 0) return maxFromDiesel;
  return Math.min(maxFromDiesel, absBal / rate);
};

const resolveDieselAwardForSelection = (totalDiesel, driverCount, selectedCount) => {
  if (!Number.isFinite(totalDiesel) || selectedCount <= 0 || driverCount <= 0) return null;
  if (selectedCount >= 2 || selectedCount === driverCount) return totalDiesel;
  return totalDiesel / driverCount;
};

const formatDriverDieselAward = (totalDiesel, driverCount) => {
  if (!Number.isFinite(totalDiesel)) {
    return { display: "—", colorClass: "text-gray-900" };
  }
  const perDriver = driverCount > 1 ? totalDiesel / driverCount : totalDiesel;
  return {
    display: `${formatLitersTruncated(perDriver)} liter`,
    colorClass:
      perDriver > 0
        ? "text-green-600"
        : perDriver < 0
          ? "text-danger"
          : "text-gray-900",
  };
};

const RequiredMark = () => <span className="text-danger"> *</span>;

const computeWaiveAmountFromLiters = (liters, rate) => {
  const l = Number(liters);
  if (!Number.isFinite(l) || l <= 0 || !rate) return 0;
  const parts = truncateAmountParts(l * rate);
  return parts?.numeric ?? 0;
};

const sanitizeWaiveLitersInput = (raw) => {
  let s = String(raw ?? "").replace(/[^\d.]/g, "");
  const dot = s.indexOf(".");
  if (dot === -1) return s;

  s = s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./g, "");
  const [intPart = "", frac = ""] = s.split(".");
  if (frac.length > 0) {
    const whole = intPart === "" ? "0" : intPart;
    return `${whole}.${frac.slice(0, 2)}`;
  }
  return intPart === "" ? "0." : `${intPart}.`;
};

const clampWaiveLitersToMax = (value, max) => {
  if (value === "" || !Number.isFinite(max) || max <= 0) return value;
  if (value.endsWith(".")) return value;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= max) return value;
  return formatLitersTruncated(max);
};
const receiptLabelFromUrl = (url) => {
  if (!url) return "—";
  try {
    const name = new URL(url).pathname.split("/").pop();
    return name || "Receipt";
  } catch {
    const segment = String(url).split("/").pop() || "";
    return segment.split("?")[0] || "Receipt";
  }
};

const revokeDraftPreviews = (drafts) => {
  Object.values(drafts || {}).forEach((d) => {
    if (d?.filePreview) URL.revokeObjectURL(d.filePreview);
  });
};

const mapExpenseFromApi = (row) => {
  const receiptUrl =
    row.recept_image ?? row.receipt_image ?? row.receipt_url ?? "";
  return {
    id: row._id,
    tripId: row.trip_id,
    amount: Number(row.amount) || 0,
    dateTime: formatDateTime(row.createdAt),
    receiptUrl,
    receiptName: receiptLabelFromUrl(receiptUrl),
    description: row.description ?? "",
    status: row.status,
    selected: false,
  };
};

const isFuelWaiveOff = (type) =>
  type === "fuel_waive_off" || type === "fuel_waive";

const resolveDriverAwardAmount = (d) => {
  const raw =
    d.award_balance_amount ??
    d.award_balance ??
    d.balance_award_amount ??
    d.award_amount;
  const n = Number(raw);
  return Number.isNaN(n) ? 0 : n;
};

const mapWaiveDriver = (d) => ({
  id: d._id,
  name: d.full_name ?? "—",
  slot: d.slot ?? "",
  userRefId: d.user_ref_id,
  awardAmount: resolveDriverAwardAmount(d),
});

const enrichDriversWithTripAwards = (drivers, tripAwards) => {
  if (!Array.isArray(tripAwards) || tripAwards.length === 0) return drivers;
  return drivers.map((driver) => {
    if (driver.awardAmount !== 0) return driver;
    const match = tripAwards.find(
      (a) =>
        a.driver_id === driver.id ||
        a._id === driver.id ||
        a.user_ref_id === driver.userRefId
    );
    if (!match) return driver;
    const amount = resolveDriverAwardAmount(match);
    if (amount === 0) return driver;
    return { ...driver, awardAmount: amount };
  });
};

const extractWaiveTripMongoId = (data) =>
  data.trip_id ??
  data._id ??
  data.trip?._id ??
  data.trip_details?._id ??
  "";

const slotLabel = (slot, index) => {
  if (slot === "primary") return "Primary Driver";
  if (slot === "secondary") return "Secondary Driver";
  return `Driver ${index + 1}`;
};

const getApiErrorMessage = (response, fallback) =>
  response?.response?.data?.errorMessage ||
  response?.response?.data?.message ||
  response?.data?.errorMessage ||
  response?.data?.message ||
  response?.data?.error ||
  (Array.isArray(response?.response?.data?.errors)
    ? response.response.data.errors.join(", ")
    : null) ||
  (Array.isArray(response?.data?.errors) ? response.data.errors.join(", ") : null) ||
  fallback;

const ACCOUNTANT_API =
  process.env.REACT_APP_ACCOUNTANT_API;

const parseFetchBody = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { rawMessage: text };
  }
};

const getFetchErrorMessage = (payload, response) => {
  if (!payload || typeof payload !== "object") {
    return response.statusText || "Something went wrong. Please try again.";
  }
  return (
    payload.message ||
    payload.errorMessage ||
    payload.error ||
    (Array.isArray(payload.errors) ? payload.errors.join(", ") : null) ||
    `Request failed (${response.status})`
  );
};

const AddNewRequest = () => {
  const navigate = useNavigate();
  const { setSubheading } = useOutletContext();
  const dropdownRef = useRef(null);
  const changeDraftRef = useRef({});

  const [requestType, setRequestType] = useState("");
  const [requestTypeId, setRequestTypeId] = useState("");
  const [requestOptions, setRequestOptions] = useState([]);
  const [requestTypesLoading, setRequestTypesLoading] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [tripId, setTripId] = useState("");

  const [expenseRows, setExpenseRows] = useState([]);
  const [expenseTripMongoId, setExpenseTripMongoId] = useState("");
  const [expenseRequestReason, setExpenseRequestReason] = useState("");
  const [expensesLoading, setExpensesLoading] = useState(false);
  const [viewDesc, setViewDesc] = useState(null);

  const [changeDraftByExpenseId, setChangeDraftByExpenseId] = useState({});
  changeDraftRef.current = changeDraftByExpenseId;

  const selectedExpenses = useMemo(
    () => expenseRows.filter((r) => r.selected),
    [expenseRows]
  );

  const [debitAmount, setDebitAmount] = useState("");
  const [debitDescription, setDebitDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [waiveDrivers, setWaiveDrivers] = useState([]);
  const [waiveTripMongoId, setWaiveTripMongoId] = useState("");
  const [waiveDriversLoading, setWaiveDriversLoading] = useState(false);
  const [selectedDriverIds, setSelectedDriverIds] = useState([]);
  const [tripAwardRate, setTripAwardRate] = useState(null);
  const [tripAwardDetails, setTripAwardDetails] = useState([]);
  const [waiveLiters, setWaiveLiters] = useState("");
  const [waiveReason, setWaiveReason] = useState("");
  const [expenseFieldErrors, setExpenseFieldErrors] = useState({});
  const [expenseReasonError, setExpenseReasonError] = useState("");
  const [expenseSelectionError, setExpenseSelectionError] = useState("");
  const [expenseTripError, setExpenseTripError] = useState("");

  useEffect(() => {
    setSubheading("Create New Request");
    return () => {
      setSubheading("");
    };
  }, [setSubheading]);

  useEffect(() => {
    const onDocDown = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadRequestTypes = async () => {
      setRequestTypesLoading(true);
      try {
        const response = await handleApiCall("get", endpoint.requestTypeList, {});
        if (cancelled) return;

        if (response?.data?.code === 200) {
          const list = response?.data?.data?.request_types;
          setRequestOptions(Array.isArray(list) ? list : []);
          return;
        }

        const errMsg =
          response?.response?.data?.message ||
          response?.data?.message ||
          "Could not load request types.";
        toast.error(errMsg);
        setRequestOptions([]);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          toast.error("Could not load request types.");
          setRequestOptions([]);
        }
      } finally {
        if (!cancelled) setRequestTypesLoading(false);
      }
    };

    loadRequestTypes();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return () => revokeDraftPreviews(changeDraftRef.current);
  }, []);

  useEffect(() => {
    if (requestType !== "expense") {
      setExpenseRows([]);
      setExpenseTripMongoId("");
      setExpenseRequestReason("");
      setChangeDraftByExpenseId((prev) => {
        revokeDraftPreviews(prev);
        return {};
      });
      setExpenseFieldErrors({});
      setExpenseReasonError("");
      setExpenseSelectionError("");
      setExpenseTripError("");
      return;
    }

    const tripRefId = tripId.trim();
    if (!tripRefId) {
      setExpenseRows([]);
      setExpenseTripMongoId("");
      setChangeDraftByExpenseId((prev) => {
        revokeDraftPreviews(prev);
        return {};
      });
      setExpenseFieldErrors({});
      setExpenseReasonError("");
      setExpenseSelectionError("");
      setExpenseTripError("");
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setExpensesLoading(true);
      try {
        const url = `${endpoint.listOfExpenceForChangeRequest}?trip_ref_id=${encodeURIComponent(tripRefId)}`;
        const response = await handleApiCall("get", url, {});
        if (cancelled) return;

        if (response?.data?.code === 200) {
          const data = response?.data?.data ?? {};
          const list = data.trip_expence_data;
          const rows = Array.isArray(list) ? list.map(mapExpenseFromApi) : [];
          const tripMongoId =
            data.trip_id ??
            data.trip?._id ??
            rows[0]?.tripId ??
            "";
          setExpenseTripMongoId(tripMongoId);
          setExpenseRows(rows);
          setChangeDraftByExpenseId((prev) => {
            revokeDraftPreviews(prev);
            return {};
          });
          return;
        }

        const errMsg =
          response?.response?.data?.message ||
          response?.data?.message ||
          "Could not load trip expenses.";
        toast.error(errMsg);
        setExpenseRows([]);
        setExpenseTripMongoId("");
        setChangeDraftByExpenseId((prev) => {
          revokeDraftPreviews(prev);
          return {};
        });
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          toast.error("Could not load trip expenses.");
          setExpenseRows([]);
          setExpenseTripMongoId("");
          setChangeDraftByExpenseId((prev) => {
            revokeDraftPreviews(prev);
            return {};
          });
        }
      } finally {
        if (!cancelled) setExpensesLoading(false);
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [requestType, tripId]);

  useEffect(() => {
    if (!isFuelWaiveOff(requestType)) {
      setWaiveDrivers([]);
      setWaiveTripMongoId("");
      setSelectedDriverIds([]);
      setTripAwardRate(null);
      setTripAwardDetails([]);
      setWaiveLiters("");
      return;
    }

    const tripRefId = tripId.trim();
    if (!tripRefId) {
      setWaiveDrivers([]);
      setWaiveTripMongoId("");
      setSelectedDriverIds([]);
      setTripAwardRate(null);
      setTripAwardDetails([]);
      setWaiveLiters("");
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setWaiveDriversLoading(true);
      try {
        const url = `${endpoint.gateDriversForWaiveOff}?trip_ref_id=${encodeURIComponent(tripRefId)}`;
        const response = await handleApiCall("get", url, {});
        if (cancelled) return;

        if (response?.data?.code === 200) {
          const data = response?.data?.data ?? {};
          const list = data.drivers;
          const driversRaw = Array.isArray(list) ? list.map(mapWaiveDriver) : [];
          const drivers = enrichDriversWithTripAwards(
            driversRaw,
            data.trip_awards
          );
          const tripMongoId = extractWaiveTripMongoId(data);
          setTripAwardRate(resolveTripAwardRate(data));
          setTripAwardDetails(
            Array.isArray(data.trip_award_details) ? data.trip_award_details : []
          );
          setWaiveTripMongoId(tripMongoId);
          setWaiveDrivers(drivers);
          setWaiveLiters("");
          setSelectedDriverIds((prev) =>
            prev.filter((id) => drivers.some((d) => d.id === id))
          );
          return;
        }

        const errMsg =
          response?.response?.data?.message ||
          response?.data?.message ||
          "Could not load drivers for waive-off.";
        toast.error(errMsg);
        setWaiveDrivers([]);
        setWaiveTripMongoId("");
        setSelectedDriverIds([]);
        setTripAwardRate(null);
        setTripAwardDetails([]);
        setWaiveLiters("");
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          toast.error("Could not load drivers for waive-off.");
          setWaiveDrivers([]);
          setWaiveTripMongoId("");
          setSelectedDriverIds([]);
          setTripAwardRate(null);
          setTripAwardDetails([]);
          setWaiveLiters("");
        }
      } finally {
        if (!cancelled) setWaiveDriversLoading(false);
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [requestType, tripId]);

  const selectedWaiveDrivers = useMemo(
    () => waiveDrivers.filter((d) => selectedDriverIds.includes(d.id)),
    [waiveDrivers, selectedDriverIds]
  );

  const dieselAwardLiters = useMemo(
    () => resolveDieselAwardLiters(tripAwardDetails),
    [tripAwardDetails]
  );

  const dieselAwardForLimit = useMemo(
    () =>
      resolveDieselAwardForSelection(
        dieselAwardLiters,
        waiveDrivers.length,
        selectedWaiveDrivers.length
      ),
    [dieselAwardLiters, waiveDrivers.length, selectedWaiveDrivers.length]
  );

  const maxWaiveLiters = useMemo(
    () =>
      computeMaxWaiveLiters(dieselAwardForLimit, selectedWaiveDrivers, tripAwardRate),
    [dieselAwardForLimit, selectedWaiveDrivers, tripAwardRate]
  );

  const canInputWaiveLiters =
    Number.isFinite(dieselAwardForLimit) &&
    dieselAwardForLimit < 0 &&
    selectedWaiveDrivers.length > 0 &&
    maxWaiveLiters > 0;

  const dieselAwardDisplay = Number.isFinite(dieselAwardLiters)
    ? `${formatLitersTruncated(dieselAwardLiters)} liter`
    : "—";

  const dieselAwardColorClass =
    Number.isFinite(dieselAwardLiters) && dieselAwardLiters > 0
      ? "text-green-600"
      : Number.isFinite(dieselAwardLiters) && dieselAwardLiters < 0
        ? "text-danger"
        : "text-gray-900";

  const selectedLabel = useMemo(
    () => requestOptions.find((o) => o.key === requestType)?.name ?? "",
    [requestType, requestOptions]
  );

  const balanceAward = selectedWaiveDrivers.reduce(
    (sum, driver) => sum + (driver.awardAmount ?? 0),
    0
  );
  const waiveAmount = useMemo(
    () => computeWaiveAmountFromLiters(waiveLiters, tripAwardRate),
    [waiveLiters, tripAwardRate]
  );

  const canSubmit = useMemo(() => {
    if (!requestType || !tripId.trim() || submitting) return false;
    if (requestType === "expense") return true;
    if (requestType === "debit_note") {
      const amount = Number(String(debitAmount).trim());
      return (
        debitDescription.trim() !== "" &&
        !Number.isNaN(amount) &&
        amount > 0
      );
    }
    if (isFuelWaiveOff(requestType)) {
      const liters = Number(String(waiveLiters).trim());
      return (
        waiveReason.trim() !== "" &&
        selectedDriverIds.length > 0 &&
        !!waiveTripMongoId &&
        waiveDrivers.length > 0 &&
        canInputWaiveLiters &&
        !Number.isNaN(liters) &&
        liters > 0 &&
        liters <= maxWaiveLiters + 1e-9 &&
        waiveAmount > 0
      );
    }
    return false;
  }, [
    requestType,
    tripId,
    submitting,
    debitAmount,
    debitDescription,
    waiveReason,
    selectedDriverIds,
    waiveTripMongoId,
    waiveDrivers,
    waiveLiters,
    maxWaiveLiters,
    canInputWaiveLiters,
    waiveAmount,
  ]);

  const clearRequestType = () => {
    setRequestType("");
    setRequestTypeId("");
    setDropdownOpen(false);
  };

  const updateChangeDraft = (expenseId, patch) => {
    setChangeDraftByExpenseId((prev) => ({
      ...prev,
      [expenseId]: { ...(prev[expenseId] || {}), ...patch },
    }));
  };

  const toggleExpenseSelect = (id) => {
    setExpenseRows((rows) => {
      const current = rows.find((r) => r.id === id);
      if (!current) return rows;
      const nextSelected = !current.selected;

      if (nextSelected) {
        setChangeDraftByExpenseId((prev) => {
          if (prev[id]) return prev;
          return {
            ...prev,
            [id]: {
              amount: String(current.amount),
              description: current.description,
              fileName: "",
            },
          };
        });
      }

      return rows.map((r) =>
        r.id === id ? { ...r, selected: nextSelected } : r
      );
    });
    if (expenseSelectionError) setExpenseSelectionError("");
  };

  const validateDebitNote = () => {
    const tripRefId = tripId.trim();
    if (!tripRefId) {
      toast.error("Enter a Trip ID.");
      return null;
    }

    const amountTrim = String(debitAmount).trim();
    const amount = Number(amountTrim);
    if (!amountTrim || Number.isNaN(amount) || amount <= 0) {
      toast.error("Enter a valid amount greater than zero.");
      return null;
    }

    const description = debitDescription.trim();
    const descriptionError = validateWordCount(description, "Description");
    if (descriptionError) {
      toast.error(descriptionError);
      return null;
    }

    if (!localStorage.getItem("sessionToken")) {
      toast.error("Session expired. Please log in again.");
      return null;
    }

    return { tripRefId, amount, description };
  };

  const submitDebitNote = async () => {
    const payload = validateDebitNote();
    if (!payload) return;

    setSubmitting(true);
    try {
      const response = await handleApiCall(
        "post",
        endpoint.createDebitNoteRequest,
        {
          trip_ref_id: payload.tripRefId,
          amount: payload.amount,
          description: payload.description,
          request_reason: payload.description,
        }
      );

      const code = response?.data?.code;
      if (code === 200 || code === 201) {
        toast.success(
          response?.data?.message || "Debit note request submitted successfully."
        );
        navigate("/my-requests", { replace: true });
        return;
      }

      toast.error(
        getApiErrorMessage(response, "Could not submit debit note request.")
      );
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.errorMessage ||
          err?.response?.data?.message ||
          err?.message ||
          "Could not submit debit note request."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const validateWaiveOff = () => {
    if (!localStorage.getItem("sessionToken")) {
      toast.error("Session expired. Please log in again.");
      return null;
    }

    if (!waiveTripMongoId) {
      toast.error(
        "Trip details not loaded. Enter Trip ID and wait for drivers to load."
      );
      return null;
    }

    const reason = waiveReason.trim();
    const reasonError = validateWordCount(reason, "Reason");
    if (reasonError) {
      toast.error(reasonError);
      return null;
    }

    if (selectedWaiveDrivers.length === 0) {
      toast.error("Select at least one driver.");
      return null;
    }

    if (!canInputWaiveLiters) {
      if (Number.isFinite(dieselAwardLiters) && dieselAwardLiters > 0) {
        toast.error("Fuel waive-off is not allowed when diesel award is positive.");
      } else {
        toast.error("Diesel award is not available for waive-off on the selected driver(s).");
      }
      return null;
    }

    const liters = Number(String(waiveLiters).trim());
    if (Number.isNaN(liters) || liters <= 0) {
      toast.error("Enter a valid fuel quantity in liters.");
      return null;
    }

    if (liters > maxWaiveLiters + 1e-9) {
      toast.error(
        `Fuel quantity cannot exceed ${formatLitersTruncated(maxWaiveLiters)} liter.`
      );
      return null;
    }

    if (waiveAmount <= 0) {
      toast.error("Waive amount must be greater than zero.");
      return null;
    }

    const primaryDriver = waiveDrivers.find((d) => d.slot === "primary");
    const secondaryDriver = waiveDrivers.find((d) => d.slot === "secondary");
    const selectedBalance = selectedWaiveDrivers.reduce(
      (sum, driver) => sum + Math.abs(Number(driver.awardAmount) || 0),
      0
    );

    const body = {
      trip_id: waiveTripMongoId,
      request_reason: reason,
      waive_fuel: liters,
      award_amount: waiveAmount,
      award_balance_amount: selectedBalance,
    };

    if (primaryDriver?.id && selectedDriverIds.includes(primaryDriver.id)) {
      body.primary_driver_id = primaryDriver.id;
    }
    if (secondaryDriver?.id && selectedDriverIds.includes(secondaryDriver.id)) {
      body.secondary_driver_id = secondaryDriver.id;
    }

    return body;
  };

  const submitWaiveOff = async () => {
    const body = validateWaiveOff();
    if (!body) return;

    setSubmitting(true);
    try {
      const response = await handleApiCall(
        "post",
        endpoint.createWaiveOffRequest,
        body
      );

      const code = response?.data?.code;
      if (code === 200 || code === 201) {
        toast.success(
          response?.data?.message ||
            "Fuel waive-off request submitted successfully."
        );
        navigate("/my-requests", { replace: true });
        return;
      }

      toast.error(
        getApiErrorMessage(response, "Could not submit fuel waive-off request.")
      );
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.errorMessage ||
          err?.response?.data?.message ||
          err?.message ||
          "Could not submit fuel waive-off request."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const validateExpenseChange = () => {
    const token = localStorage.getItem("sessionToken");
    if (!token) {
      toast.error("Session expired. Please log in again.");
      return null;
    }

    const nextFieldErrors = {};
    let nextReasonError = "";
    let nextSelectionError = "";
    let nextTripError = "";
    let hasError = false;

    if (!expenseTripMongoId) {
      nextTripError =
        "Trip details not loaded. Enter Trip ID and wait for expenses to load.";
      hasError = true;
    }

    const reason = expenseRequestReason.trim();
    const reasonError = validateWordCount(reason, "Reason");
    if (!reason) {
      nextReasonError = "Reason is required.";
      hasError = true;
    } else if (reasonError) {
      nextReasonError = reasonError;
      hasError = true;
    }

    if (selectedExpenses.length === 0) {
      nextSelectionError = "Select at least one expense to change.";
      hasError = true;
    }

    const items = [];
    const files = [];

    for (let i = 0; i < selectedExpenses.length; i += 1) {
      const exp = selectedExpenses[i];
      const draft = changeDraftByExpenseId[exp.id];
      const amountTrim = String(draft?.amount ?? "").trim();
      const amount = Number(amountTrim);
      const description = draft?.description?.trim() ?? "";
      const rowErrors = {};

      if (amountTrim === "" || Number.isNaN(amount) || amount < 0) {
        rowErrors.amount = "Enter a valid amount (0 or greater).";
        hasError = true;
      }

      if (!description) {
        rowErrors.description = "Description is required.";
        hasError = true;
      } else {
        const descriptionError = validateWordCount(
          description,
          `Description for expense ${i + 1}`
        );
        if (descriptionError) {
          rowErrors.description = descriptionError;
          hasError = true;
        }
      }

      if (!(draft?.file instanceof File)) {
        rowErrors.receipt = "Upload an updated receipt image.";
        hasError = true;
      } else if (!draft.file.type.startsWith("image/")) {
        rowErrors.receipt = "Receipt must be an image file.";
        hasError = true;
      }

      if (Object.keys(rowErrors).length > 0) {
        nextFieldErrors[exp.id] = rowErrors;
      }

      if (!hasError) {
        items.push({
          trip_expense_id: exp.id,
          amount,
          description,
        });
        files.push(draft.file);
      }
    }

    setExpenseTripError(nextTripError);
    setExpenseReasonError(nextReasonError);
    setExpenseSelectionError(nextSelectionError);
    setExpenseFieldErrors(nextFieldErrors);

    if (hasError) return null;

    return { reason, items, files };
  };

  const submitExpenseChange = async () => {
    const payload = validateExpenseChange();
    if (!payload) return;

    const token = localStorage.getItem("sessionToken");
    setSubmitting(true);

    try {
      const url = `${ACCOUNTANT_API}${endpoint.createTripExpenseChangeRequest}`;

      for (let i = 0; i < payload.items.length; i += 1) {
        const formData = new FormData();
        formData.append("trip_id", expenseTripMongoId);
        formData.append("request_reason", payload.reason);
        formData.append("items", JSON.stringify([payload.items[i]]));
        formData.append(
          "bill_image",
          payload.files[i],
          payload.files[i].name || "receipt.jpg"
        );

        const response = await fetch(url, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });

        const body = await parseFetchBody(response);

        if (!response.ok) {
          const label =
            payload.items.length > 1 ? ` for expense ${i + 1}` : "";
          toast.error(
            `${getFetchErrorMessage(body, response)}${label}`
          );
          return;
        }

        const code = body?.code;
        if (code != null && code !== 200 && code !== 201) {
          const label =
            payload.items.length > 1 ? ` for expense ${i + 1}` : "";
          toast.error(
            `${getFetchErrorMessage(body, response)}${label}`
          );
          return;
        }
      }

      toast.success("Expense change request submitted successfully.");
      navigate("/my-requests", { replace: true });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.message === "Failed to fetch"
          ? "Network error. Check your connection."
          : "Could not submit expense change request."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitRequest = async () => {
    if (submitting) return;

    if (requestType === "expense") {
      if (!requestType || !tripId.trim()) return;
      await submitExpenseChange();
      return;
    }

    if (!canSubmit) return;

    if (requestType === "debit_note") {
      await submitDebitNote();
      return;
    }

    if (isFuelWaiveOff(requestType)) {
      await submitWaiveOff();
      return;
    }

    toast.error("Submit is not available for this request type yet.");
  };

  const toggleDriverSelection = (driverId) => {
    setSelectedDriverIds((prev) => {
      const isSelected = prev.includes(driverId);
      return isSelected ? prev.filter((id) => id !== driverId) : [...prev, driverId];
    });
    setWaiveLiters("");
  };

  const clearExpenseRowError = (expenseId, field) => {
    setExpenseFieldErrors((prev) => {
      const row = prev[expenseId];
      if (!row?.[field]) return prev;
      const nextRow = { ...row, [field]: "" };
      if (!nextRow.amount && !nextRow.description && !nextRow.receipt) {
        const { [expenseId]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [expenseId]: nextRow };
    });
  };

  const handleChangeFile = (expenseId, e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    setChangeDraftByExpenseId((prev) => {
      const old = prev[expenseId];
      if (old?.filePreview) URL.revokeObjectURL(old.filePreview);
      return {
        ...prev,
        [expenseId]: {
          ...(prev[expenseId] || {}),
          fileName: f ? f.name : "",
          file: f || null,
          filePreview: f ? URL.createObjectURL(f) : "",
        },
      };
    });
    clearExpenseRowError(expenseId, "receipt");
  };

  return (
    <div className="p-6 bg-white min-h-screen font-sf-pro">
      <Toastify/>
      <Link to="/my-requests" className="btn btn-primary shrink-0 m-4">Back</Link>
      {/* Request type + trip ID */}
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-5 md:p-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:items-end">
          <div className="relative" ref={dropdownRef}>
            <label className="mb-2 block text-sm font-bold text-gray-900">
              Request Type
            </label>
            <div
              role="combobox"
              aria-expanded={dropdownOpen}
              aria-haspopup="listbox"
              onClick={() => setDropdownOpen((o) => !o)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setDropdownOpen((o) => !o);
                }
              }}
              tabIndex={0}
              className="flex w-full min-h-[48px] cursor-pointer items-center justify-between gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-[15px] shadow-sm transition hover:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <span className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                {selectedLabel ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-800">
                    {selectedLabel}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        clearRequestType();
                      }}
                      className="ml-0.5 rounded-full p-0.5 text-gray-500 hover:bg-gray-200 hover:text-gray-900"
                      aria-label="Remove request type"
                    >
                      ×
                    </button>
                  </span>
                ) : (
                  <span className="text-gray-400">
                    {requestTypesLoading
                      ? "Loading request types..."
                      : "Select Request Type"}
                  </span>
                )}
              </span>
              <span
                className="shrink-0 text-secondary"
                aria-hidden
              >
                ▼
              </span>
            </div>

            {dropdownOpen && (
              <ul
                className="absolute left-0 right-0 z-20 mt-1 max-h-60 overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg"
                role="listbox"
              >
                {requestTypesLoading ? (
                  <li className="px-4 py-2.5 text-sm text-gray-500">
                    Loading...
                  </li>
                ) : requestOptions.length === 0 ? (
                  <li className="px-4 py-2.5 text-sm text-gray-500">
                    No request types available
                  </li>
                ) : (
                  requestOptions.map((opt) => (
                    <li key={opt.request_type_id || opt.key}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={requestType === opt.key}
                        onClick={() => {
                          setRequestType(opt.key);
                          setRequestTypeId(opt.request_type_id || "");
                          setDropdownOpen(false);
                        }}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-[15px] text-gray-800 hover:bg-gray-50"
                      >
                        <span
                          className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${
                            requestType === opt.key
                              ? "border-primary bg-primary"
                              : "border-gray-400"
                          }`}
                        >
                          {requestType === opt.key ? (
                            <span className="h-1.5 w-1.5 rounded-full bg-white" />
                          ) : null}
                        </span>
                        {opt.name}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">
              Enter Trip ID
            </label>
            <input
              type="text"
              value={tripId}
              onChange={(e) => setTripId(e.target.value)}
              placeholder="e.g. TP-GO-MA-169"
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 text-[15px] text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>
      </div>

      {/* Trip Expense */}
      {requestType === "expense" && (
        <>
          <h2 className="mb-3 mt-8 text-base font-bold text-gray-900">
            Expense Details
          </h2>
          <div className="common-table-wrapper overflow-hidden rounded-lg border border-gray-200 shadow-sm">
            <div className="table-responsive">
              <table className="w-full text-sm">
                <thead className="bg-gray-700 text-white">
                  <tr>
                    <th className="p-3 text-left font-medium">S.No.</th>
                    <th className="p-3 text-left font-medium">Amount Spent</th>
                    <th className="p-3 text-left font-medium">Date &amp; Time</th>
                    <th className="p-3 text-left font-medium">Receipt Image</th>
                    <th className="p-3 text-left font-medium">Description</th>
                    <th className="p-3 text-center font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {expensesLoading ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="p-8 text-center text-sm text-gray-500"
                      >
                        Loading expenses...
                      </td>
                    </tr>
                  ) : !tripId.trim() ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="p-8 text-center text-sm text-gray-500"
                      >
                        Enter a Trip ID above to load expenses.
                      </td>
                    </tr>
                  ) : expenseRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="p-8 text-center text-sm text-gray-500"
                      >
                        No expenses found for this trip.
                      </td>
                    </tr>
                  ) : (
                    expenseRows.map((row, idx) => (
                      <tr
                        key={row.id}
                        className={`border-b border-gray-100 ${
                          idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                        }`}
                      >
                        <td className="p-3 text-gray-800">
                          {String(idx + 1).padStart(2, "0")}
                        </td>
                        <td className="p-3 text-gray-800">
                          {formatInr(row.amount)}
                        </td>
                        <td className="p-3 text-gray-700">{row.dateTime}</td>
                        <td className="p-3">
                          {row.receiptUrl ? (
                            <a
                              href={row.receiptUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex max-w-[180px] items-center gap-1.5 truncate rounded border border-gray-200 bg-gray-100 px-2 py-1 text-xs text-secondary hover:underline"
                              title={row.receiptName}
                            >
                              <span aria-hidden>🖼</span>
                              {row.receiptName}
                            </a>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="p-3">
                          <button
                            type="button"
                            onClick={() => setViewDesc(row.description)}
                            className="font-medium text-secondary hover:underline"
                          >
                            View
                          </button>
                        </td>
                        <td className="p-3 text-center">
                          <input
                            type="checkbox"
                            checked={row.selected}
                            onChange={() => toggleExpenseSelect(row.id)}
                            className="h-4 w-4 cursor-pointer rounded border-gray-400 text-primary focus:ring-primary"
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <h2 className="mt-8 text-base font-bold text-gray-900">
            Change Request
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Select rows in Expense Details above. Each selected expense gets one
            change-request block.
          </p>

          <div className="mt-4 max-w-2xl">
            <label className="mb-1 block text-sm font-bold text-gray-900">
              Reason For Change
              <RequiredMark />
            </label>
            <textarea
              rows={3}
              placeholder="Reason For Change"
              value={expenseRequestReason}
              onChange={(e) => {
                setExpenseRequestReason(limitTextInput(e.target.value));
                if (expenseReasonError) setExpenseReasonError("");
              }}
              className={`w-full resize-y rounded-md border px-3 py-2.5 text-[15px] placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary ${
                expenseReasonError ? "border-red-500" : "border-gray-300"
              }`}
            />
            {expenseReasonError ? (
              <p className="mt-1 text-sm text-red-500">{expenseReasonError}</p>
            ) : null}
            <p className="mt-1 text-xs text-gray-500">{WORD_COUNT_HINT}</p>
          </div>

          {expenseTripError ? (
            <p className="mt-3 text-sm text-red-500">{expenseTripError}</p>
          ) : null}
          {expenseSelectionError ? (
            <p className="mt-3 text-sm text-red-500">{expenseSelectionError}</p>
          ) : null}

          <div className="mt-4 space-y-6">
            {selectedExpenses.length === 0 ? (
              <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-10 text-center text-sm text-gray-500">
                No expenses selected. Use the checkboxes in Expense Details to
                add change-request rows.
              </div>
            ) : (
              selectedExpenses.map((exp, idx) => {
                const draft = changeDraftByExpenseId[exp.id] || {
                  amount: "",
                  description: "",
                  fileName: "",
                  filePreview: "",
                };
                const rowErrors = expenseFieldErrors[exp.id] || {};
                const receiptPreviewSrc =
                  draft.filePreview || exp.receiptUrl || "";
                return (
                  <div
                    key={exp.id}
                    className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start"
                  >
                    <div>
                      <p className="mb-1 text-sm font-bold text-gray-900">
                        S.No.{idx + 1}
                      </p>
                      <p className="mb-3 text-xs text-gray-500">
                        Expense: {exp.receiptName} · {exp.dateTime}
                      </p>
                      <div className="space-y-4">
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-900">
                            Amount Spent
                            <RequiredMark />
                          </label>
                          <input
                            type="text"
                            inputMode="decimal"
                            placeholder="Amount Spent"
                            value={draft.amount}
                            onChange={(e) => {
                              updateChangeDraft(exp.id, {
                                amount: e.target.value,
                              });
                              clearExpenseRowError(exp.id, "amount");
                            }}
                            className={`w-full rounded-md border px-3 py-2.5 text-[15px] placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary ${
                              rowErrors.amount ? "border-red-500" : "border-gray-300"
                            }`}
                          />
                          {rowErrors.amount ? (
                            <p className="mt-1 text-sm text-red-500">{rowErrors.amount}</p>
                          ) : null}
                        </div>
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-900">
                            Description
                            <RequiredMark />
                          </label>
                          <textarea
                            rows={4}
                            placeholder="Description"
                            value={draft.description}
                            onChange={(e) => {
                              updateChangeDraft(exp.id, {
                                description: limitTextInput(e.target.value),
                              });
                              clearExpenseRowError(exp.id, "description");
                            }}
                            className={`w-full resize-y rounded-md border px-3 py-2.5 text-[15px] placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary ${
                              rowErrors.description ? "border-red-500" : "border-gray-300"
                            }`}
                          />
                          {rowErrors.description ? (
                            <p className="mt-1 text-sm text-red-500">{rowErrors.description}</p>
                          ) : null}
                          <p className="mt-1 text-xs text-gray-500">{WORD_COUNT_HINT}</p>
                        </div>
                      </div>
                    </div>
                    <div className="lg:pt-8">
                      <label className="mb-2 block text-sm font-semibold text-secondary">
                        Upload Receipt
                        <RequiredMark />
                      </label>
                      <label
                        className={`flex cursor-pointer flex-col items-center justify-center overflow-hidden rounded-md border-2 border-dashed bg-white px-4 py-6 transition hover:border-secondary/50 hover:bg-gray-50 ${
                          rowErrors.receipt ? "border-red-500" : "border-gray-300"
                        }`}
                      >
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleChangeFile(exp.id, e)}
                        />
                        {receiptPreviewSrc ? (
                          <img
                            src={receiptPreviewSrc}
                            alt={draft.fileName || exp.receiptName || "Receipt"}
                            className="max-h-52 w-full object-contain"
                          />
                        ) : (
                          <span className="text-2xl text-gray-400" aria-hidden>
                            ⬆
                          </span>
                        )}
                        <span className="mt-3 text-center text-sm text-gray-600">
                          {draft.fileName || "Upload updated bill image"}
                        </span>
                      </label>
                      {rowErrors.receipt ? (
                        <p className="mt-1 text-sm text-red-500">{rowErrors.receipt}</p>
                      ) : null}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}

      {/* Debit Note */}
      {requestType === "debit_note" && (
        <>
          <h2 className="mb-4 mt-8 text-base font-bold text-gray-900">
            Change Request
          </h2>
          <div className="max-w-2xl space-y-4">
            <input
              type="number"
              inputMode="decimal"
              placeholder="Enter Amount*"
              value={debitAmount}
              onChange={(e) => setDebitAmount(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2.5 text-[15px] placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <textarea
              rows={5}
              placeholder="Description*"
              value={debitDescription}
              onChange={(e) => setDebitDescription(limitTextInput(e.target.value))}
              className="w-full resize-y rounded-md border border-gray-300 px-3 py-2.5 text-[15px] placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <p className="text-xs text-gray-500">{WORD_COUNT_HINT}</p>
          </div>
        </>
      )}

      {/* Fuel Waive off */}
      {isFuelWaiveOff(requestType) && (
        <>
          <p className="mt-6 text-sm font-medium text-danger">
            Important: This will create a request for admin approval to forgive
            part or all of the driver&apos;s negative balance.
          </p>

          <h2 className="mb-4 mt-8 text-base font-bold text-gray-900">
            Select Driver
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {waiveDriversLoading ? (
              <div className="col-span-full rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-10 text-center text-sm text-gray-500">
                Loading drivers...
              </div>
            ) : !tripId.trim() ? (
              <div className="col-span-full rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-10 text-center text-sm text-gray-500">
                Enter a Trip ID above to load drivers.
              </div>
            ) : waiveDrivers.length === 0 ? (
              <div className="col-span-full rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-10 text-center text-sm text-gray-500">
                No drivers found for this trip.
              </div>
            ) : (
              waiveDrivers.map((d, i) => {
                const driverDiesel = formatDriverDieselAward(
                  dieselAwardLiters,
                  waiveDrivers.length
                );
                const isSelected = selectedDriverIds.includes(d.id);
                return (
              <label
                key={d.id}
                className={`flex cursor-pointer items-center justify-between gap-3 rounded-lg border bg-gray-50 p-4 transition ${
                  isSelected
                    ? "border-primary ring-1 ring-primary"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <div>
                  <p className="text-xs font-medium text-gray-500">
                    {slotLabel(d.slot, i)}
                  </p>
                  <p className="mt-1 font-bold text-gray-900">{d.name}</p>
                  <p className="mt-1 text-sm text-gray-600">
                    Diesel Award:{" "}
                    <span className={`font-medium ${driverDiesel.colorClass}`}>
                      {driverDiesel.display}
                    </span>
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleDriverSelection(d.id)}
                  className="h-4 w-4 shrink-0 rounded border-gray-400 text-primary focus:ring-primary"
                />
              </label>
                );
              })
            )}
          </div>

          <h2 className="mb-4 mt-8 text-base font-bold text-gray-900">
            Change Request
          </h2>
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:gap-8">
            <div className="relative shrink-0 md:w-52">
              <input
                type="text"
                inputMode="decimal"
                placeholder="0"
                value={waiveLiters}
                disabled={!canInputWaiveLiters}
                onChange={(e) => {
                  const next = clampWaiveLitersToMax(
                    sanitizeWaiveLitersInput(e.target.value),
                    maxWaiveLiters
                  );
                  setWaiveLiters(next);
                }}
                className="w-full rounded-md border border-gray-300 py-2.5 pl-3 pr-14 text-[15px] focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70"
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                liter
              </span>
            </div>
            <div className="space-y-1 text-sm text-gray-700">
              <p>
                Diesel Award:{" "}
                <strong className={dieselAwardColorClass}>{dieselAwardDisplay}</strong>
              </p>
              <p>
                Waive Of Amount:{" "}
                <strong className="text-gray-900">{formatInr(waiveAmount)}</strong>
              </p>
              <p>
                Balance Award Amount:{" "}
                <strong className="text-gray-900">{formatInr(balanceAward)}</strong>
              </p>
              {selectedWaiveDrivers.length > 0 &&
                (!Number.isFinite(dieselAwardForLimit) || dieselAwardForLimit >= 0) && (
                <p className="text-sm text-danger">
                  Diesel award is not available for waive-off on the selected driver(s).
                </p>
              )}
            </div>
          </div>
          <div className="mt-4 max-w-2xl">
            <textarea
              rows={5}
              placeholder="Reason For Waive-Off"
              value={waiveReason}
              onChange={(e) => setWaiveReason(limitTextInput(e.target.value))}
              className="w-full resize-y rounded-md border border-gray-300 px-3 py-2.5 text-[15px] placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <p className="mt-1 text-xs text-gray-500">{WORD_COUNT_HINT}</p>
          </div>
        </>
      )}

      {/* Actions */}
      <div className="mt-10 flex flex-wrap gap-3">
        <Link
          to="/my-requests/"
          className="z-10 inline-flex min-w-[120px] items-center justify-center rounded-full border border-gray-300 bg-white px-5 py-2.5 text-center text-[15px] font-semibold text-gray-900 transition hover:border-primary hover:text-primary"
        >
          Cancel
        </Link>
        <button
          type="button"
          disabled={
            submitting ||
            !requestType ||
            !tripId.trim() ||
            (requestType !== "expense" && !canSubmit)
          }
          onClick={handleSubmitRequest}
          className="inline-flex min-w-[160px] items-center justify-center rounded-lg border-2 border-primary btn-primary px-5 py-2.5 text-center text-[15px] font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-300 disabled:text-gray-500"
        >
          {submitting ? "Submitting..." : "Submit Request"}
        </button>
      </div>

      {viewDesc !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="max-h-[80vh] w-full max-w-md overflow-auto rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold text-gray-900">Description</h3>
            <p className="mt-3 text-sm text-gray-700">{viewDesc}</p>
            <button
              type="button"
              onClick={() => setViewDesc(null)}
              className="mt-6 w-full rounded-lg bg-[#030253] py-2.5 text-sm font-semibold text-white"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddNewRequest;
